/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {translate} from 'utils/utility'
import type {PackageItem} from 'types/entities/PackageItem'
import type {PackagesCartItem} from 'types/entities/PackagesCartItem'
import type {PackagesItemsPerson} from 'types/entities/PackagesItems'
import {PackageItemPersonType} from 'types/enums/PackageItemPersonType'
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {getCreditItemDisplayName} from 'utils/offers/addItemToCalculationUtils'

export type OfferSelectableItem = PackageItem | PackagesItemsPerson

const CREDIT_TIER_AMOUNTS: Record<string, number> = {
    'mini standard': 32,
    'midi standard': 48,
    'maxi standard': 64,
}

const isGenericCreditShortname = (value: string): boolean =>
    value.trim().toLowerCase() === 'credit'

const buildFallbackCreditCartItemLabel = (articleShortname: string): string => {
    const trimmedShortname = articleShortname.trim()
    const knownAmount = CREDIT_TIER_AMOUNTS[trimmedShortname.toLowerCase()]

    if (knownAmount) {
        return `Credit ${trimmedShortname} (${knownAmount} credits)`
    }

    return `Credit ${trimmedShortname}`
}

export const isOfferPackageItem = (item: OfferSelectableItem): item is PackageItem => {
    return (
        'type' in item &&
        (item.type == PackageItemItemType.PackageItem ||
            item.type == PackageItemItemType.Upsell ||
            item.type == PackageItemItemType.Credit)
    )
}

export interface OfferItemPresentation {
    item: OfferSelectableItem
    title: string
    descriptionHtml: string | null
    custom_text: string | null
    imageSrc: string | null
    groupLabel: string | null
    leadingLabel: string | null
    minPersons: number | null
    maxPersons: number | null
    isChild: boolean
    subGroupName: string | null
    duration: number | null
    isKarting: boolean
}

export const getOfferItemPresentation = (item: OfferSelectableItem): OfferItemPresentation => {
    const variant = isOfferPackageItem(item) ? item.packageItemVariant : null
    const title =
        isOfferPackageItem(item) && item.type === PackageItemItemType.Credit
            ? translate(getCreditItemDisplayName(item))
            : translate(item.name)

    return {
        item: item,
        title,
        descriptionHtml: isOfferPackageItem(item)
            ? translate(item.description_html || item.description)
            : item.description
              ? translate(item.description)
              : null,
        custom_text: isOfferPackageItem(item) ? translate(item.custom_text) || item.custom_text : null,
        imageSrc: isOfferPackageItem(item) ? item.image : null,
        groupLabel: isOfferPackageItem(item) ? item.extra?.groupName || null : null,
        leadingLabel: variant ? translate(variant.name) : null,
        minPersons:
            'minimum_persons_count' in item && item.minimum_persons_count > 0 ? item.minimum_persons_count : null,
        maxPersons:
            'maximum_persons_count' in item && item.maximum_persons_count > 0 && item.maximum_persons_count < 1000
                ? item.maximum_persons_count
                : null,
        isChild: item.person_type === PackageItemPersonType.Child,
        subGroupName: isOfferPackageItem(item) ? item.extra?.subGroupName || null : null,
        duration: item.duration > 0 ? item.duration : null,
        isKarting: 'karting' in item ? item.karting : false,
    }
}

export const getPackagesCartItemDisplayName = (item: PackagesCartItem): string => {
    if (item.packageItem?.type === PackageItemItemType.Credit) {
        const articleShortname = translate(item.article_shortname as any)

        if (
            articleShortname &&
            articleShortname.trim().length > 0 &&
            !isGenericCreditShortname(articleShortname)
        ) {
            return buildFallbackCreditCartItemLabel(articleShortname)
        }

        return translate(getCreditItemDisplayName(item.packageItem))
    }

    return translate(item.packageItem?.name as any)
}
